import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from './useTenant';
import { AppRole } from '@/lib/permissions';

export interface TenantMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: AppRole;
  joined_at: string;
}

interface InviteParams {
  email: string;
  role: AppRole;
  mode: 'invite' | 'create';
  password?: string;
}

export const useTenantMembers = () => {
  const { tenantId } = useTenant();
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase.rpc('get_tenant_members', {
        p_tenant_id: tenantId,
      });
      if (fetchError) throw fetchError;
      setMembers((data ?? []) as TenantMember[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const inviteUser = useCallback(
    async (params: InviteParams) => {
      const { error: inviteError } = await supabase.functions.invoke('tenant-invite-user', {
        body: params,
      });
      if (inviteError) throw new Error(inviteError.message);
      await fetchMembers();
    },
    [fetchMembers],
  );

  const changeRole = useCallback(
    async (userId: string, role: AppRole) => {
      const { error: memberError } = await supabase
        .from('tenant_members')
        .update({ role })
        .eq('tenant_id', tenantId)
        .eq('user_id', userId);
      if (memberError) throw new Error(memberError.message);

      await supabase.from('user_roles').delete().eq('user_id', userId);
      const { error: roleError } = await supabase.from('user_roles').insert({ user_id: userId, role });
      if (roleError) throw new Error(roleError.message);

      await fetchMembers();
    },
    [tenantId, fetchMembers],
  );

  const removeMember = useCallback(
    async (userId: string) => {
      const { error: memberError } = await supabase
        .from('tenant_members')
        .delete()
        .eq('tenant_id', tenantId)
        .eq('user_id', userId);
      if (memberError) throw new Error(memberError.message);

      await supabase.from('user_roles').delete().eq('user_id', userId);
      await fetchMembers();
    },
    [tenantId, fetchMembers],
  );

  return { members, loading, error, refetch: fetchMembers, inviteUser, changeRole, removeMember };
};
